import {
  type CompensationReviewAdjustment,
  CompensationReviewCompensationItem,
  CompensationReviewRecommendationStatus,
} from "@prisma/client";
import { mapSeries } from "bluebird";
import { type AsyncReturnType } from "type-fest";
import { type SalaryRangeAutocompleteValue } from "~/components/compensation-review/SalaryRangeAutocomplete";
import { value } from "~/components/helpers";
import { sendOffCycleReviewPendingEmail } from "~/lib/external/sendinblue/templates";
import { roundTo } from "~/lib/math";
import { convertCurrency } from "~/lib/money";
import {
  type CompensationReviewContext,
  isCompensationReviewCampaignContext,
} from "~/services/compensation-review/compensationReviewContext";
import {
  CompensationReviewScopeType,
  prismaCompensationReviewScope,
} from "~/services/compensation-review/compensationReviewScope";
import { completeOffCycleRequestRequest } from "~/services/compensation-review/off-cycle-reviews/completeOffCycleRequestRequest";
import {
  computeAmountForCompensationItem,
  computeFteAmount,
} from "~/services/compensation-review/shared/compensationItems";
import { computeNewBandPositioning } from "~/services/compensation-review/shared/computeNewBandPositioning";
import { ensureFirstRecommendationIsListedToReviewer } from "~/services/compensation-review/shared/ensureFirstRecommendationIsListedToReviewer";
import { isPendingRecommendation } from "~/services/compensation-review/shared/recommendationStatus";
import { type fetchRecommendationForUpdate } from "~/services/compensation-review/shared/updateRecommendation";
import { queueEvent } from "~/workers/handleEvent";

export const submitRecommendation = async (
  ctx: CompensationReviewContext,
  params: {
    recommendation: AsyncReturnType<typeof fetchRecommendationForUpdate>;
    adjustments: CompensationReviewAdjustment[];
    recommendationInput?: {
      isPromoted: boolean;
      targetRangeAfterPromotion: SalaryRangeAutocompleteValue | null;
    };
  }
) => {
  const { recommendation, adjustments } = params;
  const { employee } = recommendation;

  if (params.recommendationInput && ctx.permissions.canUpdatePromotions) {
    await ctx.prisma.compensationReviewRecommendation.updateMany({
      where: {
        employeeId: recommendation.employee.id,
        position: { gt: recommendation.position },
      },
      data: {
        isPromoted: params.recommendationInput.isPromoted,
        targetRangeAfterPromotionId: params.recommendationInput.targetRangeAfterPromotion?.id,
      },
    });
  }

  await ctx.prisma.compensationReviewRecommendation.update({
    where: { id: recommendation.id },
    data: {
      status: CompensationReviewRecommendationStatus.SUBMITTED,
      submittedAt: new Date(),
      touchedAt: null,
    },
  });

  await mapSeries(recommendation.employee.adjustments, async (adjustment) => {
    const recommendationAdjustment = adjustments.find(
      (item) => item.budgetId === adjustment.budgetId && item.compensationItem === adjustment.compensationItem
    );

    if (!recommendationAdjustment) return;

    const submittedAmount = recommendationAdjustment.submittedAmount ?? recommendationAdjustment.recommendedAmount;
    const convertedSubmittedAmount =
      recommendationAdjustment.convertedSubmittedAmount ?? recommendationAdjustment.convertedRecommendedAmount;
    const proratedSubmittedAmount =
      recommendationAdjustment.proratedSubmittedAmount ?? recommendationAdjustment.proratedRecommendedAmount;

    await ctx.prisma.compensationReviewAdjustment.updateMany({
      where: { id: { in: [adjustment.id, recommendationAdjustment.id] } },
      data: {
        submittedAmount,
        convertedSubmittedAmount,
        proratedSubmittedAmount,
      },
    });

    await ctx.prisma.compensationReviewAdjustment.updateMany({
      where: {
        budgetId: recommendationAdjustment.budgetId,
        compensationItem: recommendationAdjustment.compensationItem,
        recommendation: {
          employeeId: recommendation.employee.id,
          position: { gt: recommendation.position },
        },
      },
      data: {
        recommendedAmount: submittedAmount,
        convertedRecommendedAmount: convertedSubmittedAmount,
        proratedRecommendedAmount: proratedSubmittedAmount,
      },
    });
  });

  const employeeAdjustments = await ctx.prisma.compensationReviewAdjustment.findMany({
    where: {
      employeeId: employee.id,
      compensationItem: {
        in: [CompensationReviewCompensationItem.BASE_SALARY, CompensationReviewCompensationItem.VARIABLE_PAY],
      },
      AND: [prismaCompensationReviewScope(ctx.scope)],
    },
  });

  const onTargetEarningsRecommendation = computeAmountForCompensationItem(
    employeeAdjustments,
    CompensationReviewCompensationItem.ON_TARGET_EARNINGS,
    ["recommendedAmount"]
  );

  const baseSalarySubmittedAmount = computeAmountForCompensationItem(
    employeeAdjustments,
    CompensationReviewCompensationItem.BASE_SALARY,
    ["submittedAmount"]
  );

  const onTargetEarningsSubmittedAmount = computeAmountForCompensationItem(
    employeeAdjustments,
    CompensationReviewCompensationItem.ON_TARGET_EARNINGS,
    ["submittedAmount"]
  );

  const isIncreaseDifferentFromRecommendation = value(() => {
    return roundTo(onTargetEarningsRecommendation, 100) !== roundTo(onTargetEarningsSubmittedAmount, 100);
  });

  const onTargetEarningsIncreasePercentage = employee.onTargetEarnings
    ? onTargetEarningsSubmittedAmount / employee.onTargetEarnings
    : null;

  const newOnTargetEarnings = employee.onTargetEarnings + onTargetEarningsSubmittedAmount;

  const newBandPositioning = computeNewBandPositioning({
    employee,
    targetRange: params.recommendationInput?.targetRangeAfterPromotion ?? employee.targetRangeAfterPromotion,
    newValues: {
      fteBaseSalary: computeFteAmount(employee.baseSalary + baseSalarySubmittedAmount, employee.fteCoefficient),
      fteOnTargetEarnings: computeFteAmount(
        employee.onTargetEarnings + onTargetEarningsSubmittedAmount,
        employee.fteCoefficient
      ),
    },
  });

  await ctx.prisma.compensationReviewEmployee.update({
    where: {
      id: employee.id,
      AND: [prismaCompensationReviewScope(ctx.scope)],
    },
    data: {
      isIncreaseDifferentFromRecommendation,
      onTargetEarningsIncreasePercentage,
      convertedNewOnTargetEarnings: convertCurrency(newOnTargetEarnings, employee.currency, ctx.parameters.currency),
      newCompaRatio: newBandPositioning?.newCompaRatio ?? null,
      newRangePenetration: newBandPositioning?.newRangePenetration ?? null,

      ...(params.recommendationInput &&
        ctx.permissions.canUpdatePromotions && {
          isPromoted: params.recommendationInput.isPromoted,
          targetRangeAfterPromotionId: params.recommendationInput.targetRangeAfterPromotion?.id,
        }),
    },
  });

  if (isPendingRecommendation(recommendation)) {
    if (isCompensationReviewCampaignContext(ctx)) {
      await queueEvent(ctx, "CompensationReview_RecommendationSubmitted", {
        campaignId: ctx.campaign.id,
        recommendationId: recommendation.id,
      });
    }

    const nextRecommendation = recommendation.employee.recommendations.find(
      (item) => item.position === recommendation.position + 1
    );

    if (nextRecommendation) {
      await ctx.prisma.compensationReviewRecommendation.update({
        where: {
          id: nextRecommendation.id,
          AND: [prismaCompensationReviewScope(ctx.scope)],
        },
        data: {
          status: CompensationReviewRecommendationStatus.PENDING_FOR_REVIEW,
          skippedByReviewerId: null,
        },
      });

      if (ctx.scope.type === CompensationReviewScopeType.OFF_CYCLE_REVIEW && nextRecommendation.reviewer) {
        await notifyNextReviewer(ctx, {
          employeeId: recommendation.employee.id,
          nextReviewerExternalEmployeeId: nextRecommendation.reviewer.externalEmployeeId,
        });
      }
    }

    if (!nextRecommendation && ctx.scope.type === CompensationReviewScopeType.OFF_CYCLE_REVIEW) {
      await completeOffCycleRequestRequest(ctx, {
        offCycleReviewConfigurationId: ctx.scope.id,
        employeeId: recommendation.employee.id,
      });
    }

    const submittedRecommendation = recommendation.employee.recommendations.find(
      (recommendation) => recommendation.status === CompensationReviewRecommendationStatus.SUBMITTED
    );

    if (submittedRecommendation) {
      await ctx.prisma.compensationReviewRecommendation.update({
        where: { id: submittedRecommendation.id },
        data: {
          status: CompensationReviewRecommendationStatus.LOCKED,
        },
      });
    }

    await ensureFirstRecommendationIsListedToReviewer(ctx, { employeeId: recommendation.employee.id });
  }
};

const notifyNextReviewer = async (
  ctx: CompensationReviewContext,
  params: {
    employeeId: number;
    nextReviewerExternalEmployeeId: number;
  }
) => {
  const nextReviewerUser = await ctx.prisma.user.findFirst({
    where: {
      companyId: ctx.user.companyId,
      permissions: {
        externalEmployeeId: params.nextReviewerExternalEmployeeId,
      },
    },
    select: { email: true, locale: true },
  });

  if (!nextReviewerUser) return;

  const offCycleReviewRequest = await ctx.prisma.offCycleReviewRequest.findFirst({
    where: {
      configurationId: ctx.scope.id,
      employeeId: params.employeeId,
    },
    include: {
      externalEmployee: true,
    },
  });

  if (!offCycleReviewRequest) return;

  await sendOffCycleReviewPendingEmail(ctx, {
    user: nextReviewerUser,
    request: offCycleReviewRequest,
  });
};
